import { DIRECTORY_FIELDS, type DirectorySource } from "@kontaktfix/shared";
import { Button, Checkbox, Field, Input, Select } from "@kontaktfix/ui";
import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DirectoryPicker } from "./directory-picker";

type Ref = DirectorySource["groups"][number];
type FilterKey = keyof DirectorySource["filters"];
const FILTERS: FilterKey[] = ["companyName", "department", "officeLocation", "country", "mailDomain"];

/** The source of a directory list (issue #26): who comes from Microsoft 365, and which fields. */
export function DirectorySourceFields({
  value,
  onChange,
}: {
  value: DirectorySource;
  onChange: (next: DirectorySource) => void;
}) {
  const { t } = useTranslation();
  const [picking, setPicking] = useState<"groups" | "include" | "exclude" | null>(null);
  const set = (patch: Partial<DirectorySource>) => onChange({ ...value, ...patch });
  const add = (key: "groups" | "include" | "exclude", ref: Ref) => {
    if (!value[key].some((r) => r.id === ref.id)) set({ [key]: [...value[key], ref] });
    setPicking(null);
  };
  const remove = (key: "groups" | "include" | "exclude", id: string) =>
    set({ [key]: value[key].filter((r) => r.id !== id) });

  return (
    <div className="flex flex-col gap-4">
      <Field label={t("lists.directory.scope")} htmlFor="directory-scope">
        <Select
          id="directory-scope"
          value={value.scope}
          onChange={(e) => set({ scope: e.target.value as DirectorySource["scope"] })}
        >
          <option value="all">{t("lists.directory.scopeAll")}</option>
          <option value="groups">{t("lists.directory.scopeGroups")}</option>
        </Select>
      </Field>
      {value.scope === "groups" && (
        <Refs
          label={t("lists.directory.groups")}
          refs={value.groups}
          onAdd={() => setPicking("groups")}
          addLabel={t("lists.directory.addGroup")}
          onRemove={(id) => remove("groups", id)}
        />
      )}

      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 text-[13px] font-medium">{t("lists.directory.filters")}</legend>
        <p className="text-[12.5px] text-fg-muted">{t("lists.directory.filtersHint")}</p>
        {FILTERS.map((key) => (
          <Field key={key} label={t(`lists.directory.filter.${key}`)} htmlFor={`filter-${key}`}>
            <Input
              id={`filter-${key}`}
              defaultValue={value.filters[key].join(", ")}
              placeholder={key === "mailDomain" ? "muster.ch" : undefined}
              onBlur={(e) =>
                set({
                  filters: {
                    ...value.filters,
                    [key]: e.target.value
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean),
                  },
                })
              }
            />
          </Field>
        ))}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-[13px] font-medium">{t("lists.directory.fieldsTitle")}</legend>
        {DIRECTORY_FIELDS.map((field) => (
          <div key={field}>
            <label className="flex cursor-pointer items-center gap-2 text-[13px]">
              <Checkbox
                checked={value.fields.includes(field)}
                onCheckedChange={(on) =>
                  set({ fields: on ? [...value.fields, field] : value.fields.filter((f) => f !== field) })
                }
                label={t(`lists.directory.fields.${field}`)}
              />
              <span>{t(`lists.directory.fields.${field}`)}</span>
            </label>
            {field === "mobilePhone" && (
              <p className="ml-6 text-[12px] text-fg-muted">{t("lists.directory.mobileHint")}</p>
            )}
          </div>
        ))}
      </fieldset>

      <Refs
        label={t("lists.directory.include")}
        hint={t("lists.directory.includeHint")}
        refs={value.include}
        onAdd={() => setPicking("include")}
        addLabel={t("lists.directory.addPerson")}
        onRemove={(id) => remove("include", id)}
      />
      <Refs
        label={t("lists.directory.exclude")}
        hint={t("lists.directory.excludeHint")}
        refs={value.exclude}
        onAdd={() => setPicking("exclude")}
        addLabel={t("lists.directory.addPerson")}
        onRemove={(id) => remove("exclude", id)}
      />

      <DirectoryPicker
        open={picking !== null}
        onOpenChange={(open) => !open && setPicking(null)}
        title={picking === "groups" ? t("lists.directory.addGroup") : t("lists.directory.addPerson")}
        type={picking === "groups" ? "group" : "user"}
        excluded={picking ? value[picking].map((r) => r.id) : []}
        onPick={(entry) => {
          if (picking) add(picking, { id: entry.id, name: entry.displayName });
        }}
      />
    </div>
  );
}

function Refs({
  label,
  hint,
  refs,
  onAdd,
  addLabel,
  onRemove,
}: {
  label: string;
  hint?: string;
  refs: Ref[];
  onAdd: () => void;
  addLabel: string;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[13px] font-medium">{label}</div>
      {hint && <p className="text-[12.5px] text-fg-muted">{hint}</p>}
      {refs.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {refs.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-1 rounded-md border border-border bg-subtle py-0.5 pr-1 pl-2 text-[12.5px]"
            >
              {r.name || r.id}
              <button
                type="button"
                onClick={() => onRemove(r.id)}
                aria-label={t("common.remove")}
                className="rounded p-0.5 text-fg-muted hover:text-fg"
              >
                <XIcon className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button type="button" size="sm" onClick={onAdd}>
          <PlusIcon />
          {addLabel}
        </Button>
      </div>
    </div>
  );
}
